import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

import type { LoadedCharacterMesh } from '../../gltf/types';
import {
  coerceOpaqueBlendMaterials,
  stripMorphTargetsFromScene,
} from '../../gltf/assetHygiene';
import {
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '../../gltf/utils';

import { fetchBonesMapWithCredentials } from './fetchBonesMap';
import type { LoadFBXMesh, TransformLoadedMesh } from './useCharacterMeshWithCredentials';

async function fetchFbxBuffer(meshPath: string): Promise<ArrayBuffer> {
  const response = await fetch(meshPath);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.arrayBuffer();
}

function buildLoadedCharacterMesh(
  scene: THREE.Group,
  bonesMap: LoadedCharacterMesh['bonesMap'],
): LoadedCharacterMesh {
  const nodes: LoadedCharacterMesh['nodes'] = {};
  const materials: LoadedCharacterMesh['materials'] = {};
  const meshes: LoadedCharacterMesh['meshes'] = {};

  scene.traverse((object: THREE.Object3D) => {
    if (object.name) nodes[object.name] = object;

    const mesh = object as THREE.Mesh;
    if (mesh.material) {
      const material = Array.isArray(mesh.material)
        ? mesh.material[0]
        : mesh.material;
      if (material?.name) materials[material.name] = material;
    }

    if (object instanceof THREE.Mesh && object.name) {
      meshes[object.name] = object;
    }
  });

  scene.traverse((object: THREE.Object3D) => {
    if (object instanceof THREE.Bone && object.name) {
      const originalName = bonesMap[object.name] ?? object.name;
      object.userData.originalName = originalName;
      object.userData.autoAssign = object.name;
    }
  });

  const result: LoadedCharacterMesh = {
    scene,
    nodes,
    materials,
    meshes,
    bonesMap,
  };

  hideMeshMaterials(result);
  turnOnShadows(result);
  disableFrustumCulled(result);

  return result;
}

/**
 * Default Player FBX loader: `fetch` for presigned URLs, Three.js parser.
 * Applets can register {@link cartographerFbxCharacterMeshLoader} for Cartographer parity.
 */
export const loadFbxCharacterMesh: LoadFBXMesh = async (path) => {
  const buffer = await fetchFbxBuffer(path);
  const loader = new FBXLoader();
  const scene = loader.parse(buffer, '') as THREE.Group;
  const bonesMap = await fetchBonesMapWithCredentials(path);
  return buildLoadedCharacterMesh(scene, bonesMap);
};

export function createFbxCharacterMeshTransform(options: {
  meshPath: string;
}): TransformLoadedMesh {
  const { meshPath } = options;

  return async (meshData) => {
    if (!(meshData.scene instanceof THREE.Group)) return;

    if (!meshPath) return;

    coerceOpaqueBlendMaterials(meshData.scene);
    stripMorphTargetsFromScene(meshData.scene);
  };
}

export function createLoadFbxCharacterMesh(): LoadFBXMesh {
  return loadFbxCharacterMesh;
}
